import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Table,
  Tabs,
  Td,
} from '../components/ui/primitives';
import { MicrosoftPanel, type MicrosoftReport } from '../components/deliverability/MicrosoftPanel';
import { Link } from 'react-router-dom';
import { dateTime, relative } from '../lib/format';
import type { Alert, ClientRecord } from '../lib/types';

export function DeliverabilityPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('microsoft');
  const [clientId, setClientId] = useState('');
  const [days, setDays] = useState(30);

  const filter = qs({ clientId, days });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ clients: ClientRecord[] }>('/api/clients'),
  });
  const { data: microsoft } = useQuery({
    queryKey: ['microsoft', filter],
    queryFn: () => api.get<MicrosoftReport>('/api/analytics/microsoft' + filter),
  });
  const { data: dmarc } = useQuery({
    queryKey: ['dmarc-overview'],
    queryFn: () =>
      api.get<{
        domains: Array<{
          domainId: string;
          domain: string;
          checkedAt: string | null;
          analysed: boolean;
          policy: 'none' | 'quarantine' | 'reject' | null;
          enforcement: string | null;
          inherited: boolean;
          alignmentRisk: 'low' | 'medium' | 'high' | null;
          score: number | null;
          grade: string | null;
          summary: string;
          critical: number;
          warnings: number;
        }>;
        totals: {
          domains: number;
          enforcing: number;
          monitoringOnly: number;
          unprotected: number;
          notScanned: number;
          critical: number;
          warnings: number;
        };
      }>('/api/dmarc'),
  });

  const { data: dmarcReports } = useQuery({
    queryKey: ['dmarc-report-coverage'],
    queryFn: () =>
      api.get<{
        mailboxConfigured: boolean;
        domains: Array<{
          domainId: string;
          domain: string;
          policyDomain: string;
          viaOrganizationalDomain: boolean;
          daysCovered: number;
          reporters: number;
          reports: number;
          latest: string | null;
          sources: number;
          readiness: { safeToEnforce: boolean; summary: string; unalignedMessages: number; totalMessages: number };
        }>;
        unmanaged: Array<{ policyDomain: string; reports: number; messages: number; latest: string }>;
      }>('/api/dmarc/reports'),
  });

  const { data: alerts } = useQuery({
    queryKey: ['alerts', 'all'],
    queryFn: () => api.get<{ alerts: Alert[] }>('/api/alerts?status=all'),
  });
  const { data: blocklists } = useQuery({
    queryKey: ['blocklists'],
    queryFn: () =>
      api.get<{
        monitoredZones: Array<{ provider: string; zone: string; kind: string; critical: boolean; delistUrl: string | null }>;
        listings: Array<{
          subjectValue: string;
          provider: string;
          zone: string;
          delistUrl: string | null;
          critical: boolean;
          checkedAt: string;
        }>;
      }>('/api/blocklists'),
  });
  const { data: seeds } = useQuery({
    queryKey: ['seed-accounts'],
    queryFn: () =>
      api.get<{
        seedAccounts: Array<{
          id: string;
          email: string;
          providerFamily: string;
          protocol: string;
          status: string;
          lastCheckedAt: string | null;
          lastError: string | null;
        }>;
      }>('/api/seed-accounts'),
  });

  const act = useMutation({
    mutationFn: (input: { id: string; action: string }) =>
      api.post('/api/alerts/' + input.id + '/' + input.action),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  });

  return (
    <>
      <PageHeader
        title="Deliverability"
        description="Microsoft-specific monitoring, blocklist status, seed mailboxes and the alerts raised against every sending domain."
      />

      <div className="mb-5 flex flex-wrap items-end gap-3">
        <Field label="Client">
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">All clients</option>
            {(clients?.clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Period">
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </Select>
        </Field>
      </div>

      <Tabs
        tabs={[
          { id: 'microsoft', label: 'Microsoft' },
          { id: 'dmarc', label: 'DMARC' },
          { id: 'alerts', label: 'Alerts' },
          { id: 'blocklists', label: 'Blocklists' },
          { id: 'seeds', label: 'Seed mailboxes' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'microsoft' && (microsoft ? <MicrosoftPanel report={microsoft} /> : <Loading />)}

      {tab === 'dmarc' && (
        <Card
          title="DMARC across every sending domain"
          subtitle={
            dmarc
              ? dmarc.totals.enforcing +
                ' of ' +
                dmarc.totals.domains +
                ' domains enforce a policy. ' +
                dmarc.totals.critical +
                ' critical problem(s) outstanding. Scanned automatically every morning at 05:30.'
              : 'Scanned automatically every morning at 05:30.'
          }
        >
          {!dmarc?.domains.length ? (
            <EmptyState title="No sending domains yet" />
          ) : (
            <Table head={['Domain', 'Policy', 'Alignment risk', 'Problems', 'Scanned', 'Summary']}>
              {dmarc.domains.map((d) => (
                <tr key={d.domainId}>
                  <Td className="font-medium text-ink-900">
                    <Link to={'/domains/' + d.domainId} className="hover:underline">
                      {d.domain}
                    </Link>
                  </Td>
                  <Td>
                    {!d.analysed ? (
                      <Badge>not scanned</Badge>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Badge
                          tone={
                            d.policy === 'reject' ? 'good' : d.policy === 'quarantine' ? 'warn' : 'bad'
                          }
                        >
                          p={d.policy ?? 'none'}
                        </Badge>
                        {d.inherited && <Badge tone="info">inherited</Badge>}
                        {d.enforcement === 'partial' && <Badge tone="warn">partial</Badge>}
                      </div>
                    )}
                  </Td>
                  <Td>
                    {d.alignmentRisk && (
                      <Badge
                        tone={
                          d.alignmentRisk === 'low'
                            ? 'good'
                            : d.alignmentRisk === 'medium'
                              ? 'warn'
                              : 'bad'
                        }
                      >
                        {d.alignmentRisk}
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    <div className="flex gap-1.5">
                      {d.critical > 0 && <Badge tone="bad">{d.critical} critical</Badge>}
                      {d.warnings > 0 && <Badge tone="warn">{d.warnings} warning</Badge>}
                      {d.analysed && d.critical === 0 && d.warnings === 0 && (
                        <Badge tone="good">clear</Badge>
                      )}
                    </div>
                  </Td>
                  <Td className="whitespace-nowrap text-xs">
                    {d.checkedAt ? relative(d.checkedAt) : '--'}
                  </Td>
                  <Td className="max-w-md text-xs leading-relaxed text-ink-500">{d.summary}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {tab === 'dmarc' && dmarcReports && (
        <div className="mt-5">
          <Card
            title="Aggregate report coverage"
            subtitle={
              dmarcReports.mailboxConfigured
                ? 'Collected from the reporting mailbox every six hours. Receivers batch a day at a time, so expect a 24-48 hour lag.'
                : 'No reporting mailbox is configured, so no reports are being collected.'
            }
          >
            {!dmarcReports.mailboxConfigured ? (
              <EmptyState
                title="No reporting mailbox"
                body="Point every domain's rua= tag at one mailbox, then add it under Settings - Connected services - Dmarc. There is no API: receivers only ever email these reports."
              />
            ) : (
              <Table head={['Domain', 'Reports', 'Receivers', 'Days', 'Messages', 'Safe to enforce', 'Latest']}>
                {dmarcReports.domains.map((d) => (
                  <tr key={d.domainId}>
                    <Td className="font-medium text-ink-900">
                      <Link to={'/domains/' + d.domainId} className="hover:underline">
                        {d.domain}
                      </Link>
                      {d.viaOrganizationalDomain && (
                        <div className="mt-0.5 font-mono text-[10px] text-ink-400">
                          filed under {d.policyDomain}
                        </div>
                      )}
                    </Td>
                    <Td className="tabular-nums">{d.reports}</Td>
                    <Td className="tabular-nums">{d.reporters}</Td>
                    <Td className="tabular-nums">{d.daysCovered}</Td>
                    <Td className="tabular-nums">{d.readiness.totalMessages.toLocaleString()}</Td>
                    <Td>
                      {!d.reports ? (
                        <Badge>no data</Badge>
                      ) : d.readiness.safeToEnforce ? (
                        <Badge tone="good">yes</Badge>
                      ) : d.readiness.unalignedMessages ? (
                        <Badge tone="bad">
                          {d.readiness.unalignedMessages.toLocaleString()} would break
                        </Badge>
                      ) : (
                        <Badge tone="warn">not enough data</Badge>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap text-xs">
                      {d.latest ? relative(d.latest) : '--'}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}

            {dmarcReports.unmanaged.length > 0 && (
              <div className="mt-5 rounded-lg border border-sky-200 bg-sky-50 p-3">
                <p className="text-xs font-medium text-sky-900">
                  Reports are arriving for domains that are not registered here
                </p>
                <p className="mt-1 text-xs leading-relaxed text-sky-900">
                  Usually a domain someone forgot to add. The reports are stored either way, so
                  nothing is lost by adding it now.
                </p>
                <ul className="mt-2 space-y-1">
                  {dmarcReports.unmanaged.map((u) => (
                    <li key={u.policyDomain} className="font-mono text-xs text-sky-900">
                      {u.policyDomain} - {u.reports} report(s), {u.messages.toLocaleString()} message(s)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === 'alerts' && (
        <Card title="Alerts">
          {!alerts?.alerts.length ? (
            <EmptyState title="No alerts" />
          ) : (
            <Table head={['Severity', 'Category', 'Alert', 'Raised', 'Status', '']}>
              {alerts.alerts.map((a) => (
                <tr key={a.id}>
                  <Td>
                    <Badge tone={a.severity === 'critical' ? 'bad' : a.severity === 'warning' ? 'warn' : 'info'}>
                      {a.severity}
                    </Badge>
                  </Td>
                  <Td className="text-xs capitalize">{a.category}</Td>
                  <Td className="max-w-xl">
                    <div className="font-medium text-ink-900">{a.title}</div>
                    <p className="mt-0.5 text-xs leading-relaxed text-ink-500">{a.body}</p>
                  </Td>
                  <Td className="whitespace-nowrap text-xs">{relative(a.createdAt)}</Td>
                  <Td>
                    <Badge tone={a.status === 'open' ? 'warn' : 'neutral'}>{a.status}</Badge>
                  </Td>
                  <Td>
                    {a.status === 'open' && (
                      <div className="flex gap-1.5">
                        <Button onClick={() => act.mutate({ id: a.id, action: 'ack' })}>Ack</Button>
                        <Button onClick={() => act.mutate({ id: a.id, action: 'resolve' })}>Resolve</Button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {tab === 'blocklists' && (
        <div className="space-y-5">
          <Card
            title="Current listings"
            subtitle="Queried directly over DNS on a schedule. A listing on a critical zone quarantines the domain automatically."
          >
            {!blocklists?.listings.filter((l) => l.critical || true).length ? (
              <EmptyState title="No listings recorded" />
            ) : (
              <Table head={['Subject', 'List', 'Zone', 'Critical', 'Checked', 'Delist']}>
                {blocklists.listings.map((l) => (
                  <tr key={l.subjectValue + l.provider + l.zone}>
                    <Td className="font-mono text-xs">{l.subjectValue}</Td>
                    <Td className="font-medium text-ink-900">{l.provider}</Td>
                    <Td className="font-mono text-[11px] text-ink-500">{l.zone}</Td>
                    <Td>{l.critical ? <Badge tone="bad">critical</Badge> : <Badge>minor</Badge>}</Td>
                    <Td className="text-xs">{dateTime(l.checkedAt)}</Td>
                    <Td>
                      {l.delistUrl && (
                        <a href={l.delistUrl} target="_blank" rel="noreferrer" className="text-xs text-sky-700 underline">
                          Request removal
                        </a>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>

          <Card title="Monitored zones" subtitle="No API key required - these are plain DNS queries.">
            <Table head={['Provider', 'Zone', 'Applies to', 'Weight']}>
              {(blocklists?.monitoredZones ?? []).map((z) => (
                <tr key={z.zone}>
                  <Td className="font-medium text-ink-900">{z.provider}</Td>
                  <Td className="font-mono text-xs">{z.zone}</Td>
                  <Td className="text-xs capitalize">{z.kind}</Td>
                  <Td>{z.critical ? <Badge tone="bad">critical</Badge> : <Badge>minor</Badge>}</Td>
                </tr>
              ))}
            </Table>
          </Card>
        </div>
      )}

      {tab === 'seeds' && <SeedAccountsPanel accounts={seeds?.seedAccounts ?? []} />}
    </>
  );
}

function SeedAccountsPanel({
  accounts,
}: {
  accounts: Array<{
    id: string;
    email: string;
    providerFamily: string;
    protocol: string;
    status: string;
    lastCheckedAt: string | null;
    lastError: string | null;
  }>;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    email: '',
    providerFamily: 'microsoft',
    protocol: 'imap',
    label: '',
    host: 'outlook.office365.com',
    port: '993',
    user: '',
    password: '',
    tenantId: '',
    clientId: '',
    clientSecret: '',
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/seed-accounts', {
        email: form.email,
        providerFamily: form.providerFamily,
        protocol: form.protocol,
        label: form.label,
        config:
          form.protocol === 'imap'
            ? { host: form.host, port: form.port, user: form.user || form.email, password: form.password }
            : { tenantId: form.tenantId, clientId: form.clientId, clientSecret: form.clientSecret },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['seed-accounts'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete('/api/seed-accounts/' + id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['seed-accounts'] }),
  });

  return (
    <div className="space-y-5">
      <Card
        title="Seed mailboxes"
        subtitle="Mailboxes you control at each provider. Reading them is the only way to know whether a message reached the inbox or the junk folder."
      >
        {!accounts.length ? (
          <EmptyState
            title="No seed mailboxes configured"
            body="Until at least one Microsoft mailbox is added, inbox placement is reported as 'not measured' rather than estimated."
          />
        ) : (
          <Table head={['Mailbox', 'Provider', 'Transport', 'Status', 'Last checked', '']}>
            {accounts.map((a) => (
              <tr key={a.id}>
                <Td className="font-mono text-xs">{a.email}</Td>
                <Td>
                  <Badge tone={a.providerFamily === 'microsoft' ? 'info' : 'neutral'}>{a.providerFamily}</Badge>
                </Td>
                <Td className="text-xs uppercase">{a.protocol}</Td>
                <Td>
                  <Badge tone={a.status === 'active' ? 'good' : 'bad'}>{a.status}</Badge>
                  {a.lastError && <div className="mt-1 text-[11px] text-red-600">{a.lastError}</div>}
                </Td>
                <Td className="text-xs">{a.lastCheckedAt ? relative(a.lastCheckedAt) : 'never'}</Td>
                <Td>
                  <Button variant="ghost" onClick={() => remove.mutate(a.id)}>
                    Remove
                  </Button>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="Add a seed mailbox">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Mailbox address">
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Provider">
            <Select
              value={form.providerFamily}
              onChange={(e) => setForm({ ...form, providerFamily: e.target.value })}
            >
              <option value="microsoft">Microsoft (Outlook / M365)</option>
              <option value="google">Google</option>
              <option value="yahoo">Yahoo</option>
              <option value="other">Other</option>
            </Select>
          </Field>
          <Field label="Transport" hint="Use Graph for Microsoft 365 tenants where IMAP basic auth is off.">
            <Select value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })}>
              <option value="imap">IMAP</option>
              <option value="graph">Microsoft Graph</option>
            </Select>
          </Field>
          <Field label="Label">
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Field>

          {form.protocol === 'imap' ? (
            <>
              <Field label="IMAP host">
                <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} />
              </Field>
              <Field label="Port">
                <Input value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} />
              </Field>
              <Field label="Username">
                <Input value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} />
              </Field>
              <Field label="App password" hint="Stored encrypted; never returned by the API.">
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Tenant ID">
                <Input value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} />
              </Field>
              <Field label="Client ID">
                <Input value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} />
              </Field>
              <Field label="Client secret">
                <Input
                  type="password"
                  value={form.clientSecret}
                  onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
                />
              </Field>
            </>
          )}
        </div>
        <div className="mt-4">
          <Button variant="primary" loading={create.isPending} disabled={!form.email} onClick={() => create.mutate()}>
            Add mailbox
          </Button>
        </div>
      </Card>
    </div>
  );
}
